import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  DialogContent,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  TopSection,
  ProfileImageWrapper,
  ProfileImageInnerWrapper,
  TextWrapper,
  Divider,
  ToolTipStyled,
} from "./styles";
import TextView from "../../../../TextView";
import SearchField from "../../ListComponents/SearchField";
import { tokens, colors } from "../../../../../utils";
import ReuseableButton from "../../../Button";
import { addFolder, updateFolder } from "../../../../../redux/reducers/gallery";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createSubmitGuard } from "../../../../../utils/submitGuard";
// import { v4 as uuidv4 } from "uuid";
import {
  useCreateFolder,
  useUpdateFolder,
} from "../../../../../services/mutations";
import BackdropSpinner from "../../ListComponents/BackdropSpinner";
import CustomTooltip from "../../../../SegmentBuilder/Shared/CustomTooltip";
import CustomSwitch from "../../../../CustomSwitch";
import Tooltip from "@mui/material/Tooltip";
// Gordon 2026-08-27 change 1: reuse the SAME Public/Private/Hidden picker (and
// the SAME wire values -- 1/2/3, matching Wheel.visibility) a Wheel uses, so a
// Collection's visibility never drifts into its own bespoke model again.
import WheelVisibilityOptions from "../../../../WheelVisibilityOptions";
import { WHEEL_VISIBILITY } from "../../../../../constants/wheelVisibility";
// #920 §1: the create/edit dialog is user-facing gallery UI, so its wording
// comes from the shared Collections vocabulary. The folder MODEL and every
// payload key below (`name`, `visibility`, `gallery_visibility`) are unchanged.
import {
  COLLECTION_DIALOG_COPY,
  COLLECTION_TITLE_MAX_LENGTH,
  COLLECTION_DESCRIPTION_MAX_LENGTH,
} from "../../FoldersV2/collectionsCopy";

export let CreateFolderDialogProps = {};
const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  ".MuiPaper-root": {
    border: "1px solid #E1E1E1",
    borderRadius: 8,
    width: "40vw",
    // height: "68vh",
  },
}));

const CreateFolderDialog = ({ onCancel = () => {} }) => {
  const [open, setOpen] = useState(false);
  const [folderTitle, setFolderTitle] = useState("");
  // BUG-953: shown in the collection's Share preview (og:description).
  const [folderDescription, setFolderDescription] = useState("");
  const [folderId, setFolderId] = useState(null);
  const createFolder = useCreateFolder();
  const _updateFolder = useUpdateFolder();
  const [parentId, setParentId] = useState(null);
  const [loader, setLoader] = useState(false);
  const [exclude, setExclude] = useState(false);
  const [visibility, setVisibility] = useState(WHEEL_VISIBILITY.PUBLIC);

  // gordon-0921-WEB-collection-kebab-...-update-button-dirty-gate: a snapshot
  // of the values EDIT mode was seeded with (set once, in `setFolderFormData`,
  // never touched by subsequent typing) so `isDirty` below can compare
  // current-vs-initial without a second piece of form-library state. null in
  // CREATE mode -- the Update-button gate only ever applies while `folderId`
  // is set, so a null snapshot is never read.
  const initialValuesRef = useRef(null);

  // Synchronous re-entrancy guard for handleCreate's async mutate() calls --
  // same pattern (and the same measured login double-submit defect) as
  // src/pages/Auth/Login/index.jsx; see src/utils/submitGuard.js.
  const submitInFlight = useRef(false);
  const submitGuard = createSubmitGuard(submitInFlight);

  const dispatch = useDispatch();
  const handleCancel = () => {
    setOpen(false);
    resetState();
    onCancel && onCancel();
  };

  const formOpen = () => setOpen(true);
  const formClose = () => {
    setOpen(false);
    resetState();
  };
  const resetState = () => {
    setFolderId(null);
    setFolderTitle("");
    setFolderDescription("");
    setExclude(false);
    setVisibility(WHEEL_VISIBILITY.PUBLIC);
    initialValuesRef.current = null;
  };

  const handleParentId = (id) => {
    setParentId(id);
  };
  const setFolderFormData = (data = {}) => {
    const {
      title,
      description,
      visibility: folderVisibility,
      id,
      gallery_visibility,
    } = data || {};
    const seededTitle = title || "";
    const seededDescription = description || "";
    const seededExclude = !!gallery_visibility;
    const seededVisibility = folderVisibility || WHEEL_VISIBILITY.PUBLIC;
    setFolderTitle(seededTitle);
    setFolderDescription(seededDescription);
    setExclude(seededExclude);
    setVisibility(seededVisibility);
    setFolderId(id);
    initialValuesRef.current = {
      title: seededTitle.trim(),
      description: seededDescription,
      exclude: seededExclude,
      visibility: seededVisibility,
    };
  };

  useEffect(() => {
    CreateFolderDialogProps = {
      formOpen,
      formClose,
      handleCancel,
      setFolderFormData,
      handleParentId,
      resetState,
    };
    return () => {
      CreateFolderDialogProps = {};
      setFolderId(null);
      setExclude(false);
    };
  }, []);

  // Reusing WheelVisibilityOptions' native RadioGroup contract: the handler
  // receives a real change event, not a bare type string.
  const handleVisibilityChange = (event) => {
    setVisibility(Number(event.target.value));
  };

  const resetForm = () => {
    setFolderId(null);
    setFolderTitle("");
    setFolderDescription("");
    setExclude(false);
    setVisibility(WHEEL_VISIBILITY.PUBLIC);
    initialValuesRef.current = null;
  };

  // Gordon 2026-09-21 (second note, Edit Collection dialog screenshot):
  // "Update button should be grayed out if the user didn't make any change."
  // One isDirty, current-vs-the-snapshot `setFolderFormData` took at open --
  // no second/hand-rolled dirty tracker.
  const isDirty =
    !initialValuesRef.current ||
    folderTitle.trim() !== initialValuesRef.current.title ||
    folderDescription !== initialValuesRef.current.description ||
    exclude !== initialValuesRef.current.exclude ||
    visibility !== initialValuesRef.current.visibility;

  // Only EDIT mode (folderId set) is gated -- Create keeps its own pre-existing
  // rule (needs a name, enforced by the toast in handleCreate below).
  const isEditing = Boolean(folderId);
  const updateDisabled =
    isEditing && (!isDirty || !folderTitle.trim() || loader);

  const handleCreate = () => {
    if (!folderTitle)
      return toast.error(COLLECTION_DIALOG_COPY.titleRequiredError);
    if (!submitGuard.tryAcquire()) return;

    setLoader(true);
    if (!folderId) {
      let _payload = {
        name: folderTitle,
        description: folderDescription,
        visibility,
        parent: parentId,
        tags: [{ name: "Important" }],
        gallery_visibility: exclude,
      };
      createFolder.mutate(_payload, {
        onSuccess: (data) => {
          toast.success(COLLECTION_DIALOG_COPY.createdToast);
          const payload = {
            ..._payload,
            title: folderTitle,
            description: folderDescription,
            parentId,
            id: data?.data?.data?.id,
            isPrimary: false,
            type: "folder",
            items: [],
            date: data?.data?.data?.updated_at,
            size: 0,
            content_amount: 0,
            gallery_visibility: exclude,
            tags: _payload?.tags?.map((tag) => tag.name),
          };
          dispatch(addFolder(payload));
          setParentId(null);
          resetForm();
          setOpen(false);
        },
        onError: (error) => {
          console.log(error);
          toast.error("Something went wrong");
        },
        onSettled: () => {
          setLoader(false);
          submitGuard.release();
        },
      });
    } else {
      let _payload = {
        id: folderId,
        name: folderTitle,
        description: folderDescription,
        visibility,
        gallery_visibility: exclude,
      };
      _updateFolder.mutate(_payload, {
        onSuccess: () => {
          dispatch(
            updateFolder({
              id: folderId,
              updates: {
                title: folderTitle,
                description: folderDescription,
                visibility,
                gallery_visibility: exclude,
              },
            })
          );
          resetForm();
          setOpen(false);
        },
        onError: (error) => {
          console.log({ updateError: error });
          toast.error("Something went wrong");
        },
        onSettled: () => {
          setLoader(false);
          submitGuard.release();
        },
      });
    }

    // const payload = {
    //   id: uuidv4(),
    //   title: folderTitle,
    //   visibility: getPrimaryStatus(),
    //   isPrimary: false,
    //   isFavorite: false,
    //   type: "folder",
    //   items: [],
    //   parentId: null,
    //   date: new Date().toISOString(),
    //   size: 0,
    //   content_amount: 0,
    //   tags: ["food", "nature", "people"],
    // };

    // folderId
    //   ? dispatch(
    //       updateFolder({
    //         id: folderId,
    //         updates: {
    //           title: folderTitle,
    //           visibility,
    //         },
    //       })
    //     )
    //   : dispatch(addFolder(payload));
  };

  return (
    <>
      <BackdropSpinner open={loader} />
      <StyledDialog
        open={open}
        onClose={formClose}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <DialogTitle
          sx={{
            padding: "5px 10px",
          }}
        >
          <TopSection>
            <ProfileImageWrapper>
              <ProfileImageInnerWrapper>
                {/* Gordon 2026-08-27 change 4: the Create Collection header no
                    longer carries a folder+ icon -- only Edit keeps its icon,
                    since only Create was called out. */}
                {folderId && (
                  <div style={{ paddingTop: 7 }}>
                    <EditIcon
                      style={{
                        fontSize: 28,
                        color: "black",
                      }}
                    />
                  </div>
                )}
                <TextWrapper>
                  <TextView variant="title">
                    {folderId
                      ? COLLECTION_DIALOG_COPY.editTitle
                      : COLLECTION_DIALOG_COPY.createTitle}
                  </TextView>
                </TextWrapper>
              </ProfileImageInnerWrapper>
            </ProfileImageWrapper>
            <IconButton aria-label="close" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </TopSection>
        </DialogTitle>
        <Divider />

        <DialogContent>
          <SearchField
            height={48}
            placeholder={COLLECTION_DIALOG_COPY.titleFieldPlaceholder}
            isHideIcon={true}
            // QA-1261 — cap the Collection name so it can't be created unbounded.
            maxLength={COLLECTION_TITLE_MAX_LENGTH}
            value={folderTitle}
            onChange={(val) => setFolderTitle(val)}
            // Without this the shared SearchField's default leaks through and a
            // screen reader announces this field as "Search wheels" (measured).
            ariaLabel={COLLECTION_DIALOG_COPY.titleFieldAriaLabel}
          />

          {/* BUG-953: shown in the Share preview's og:description. */}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            variant="outlined"
            placeholder={COLLECTION_DIALOG_COPY.descriptionFieldPlaceholder}
            aria-label={COLLECTION_DIALOG_COPY.descriptionFieldAriaLabel}
            value={folderDescription}
            onChange={(e) =>
              setFolderDescription(
                e.target.value.slice(0, COLLECTION_DESCRIPTION_MAX_LENGTH)
              )
            }
            sx={{
              marginTop: "10px",
              // slack-dev-0902-collections-inputs-purple-focus-ring — measured
              // live: this field's Mui-focused state repainted
              // `.MuiOutlinedInput-notchedOutline` purple on its own, while
              // MUI's baked-in `.MuiInputBase-input:focus { outline: 0 }`
              // canceled the app-wide shared purple ring (src/styles/focusRing.css),
              // so the Title field right above it kept the correct double
              // ring and this one lost the purple outline entirely (only the
              // white inner band survived). Same fix as SearchField.jsx's
              // ISP-9999 gordon-0831-gallery-search-double-focus-outline: pin
              // the fieldset border to its resting color at every state and
              // paint the ring explicitly.
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(0, 0, 0, 0.23)",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
              "& .MuiOutlinedInput-input:focus-visible": {
                outline: `${tokens.focusRing.width}px solid ${tokens.focusRing.color}`,
                outlineOffset: tokens.focusRing.innerWidth,
              },
            }}
          />

          {/* Start Exclude toggle */}
          <div style={{ display: "flex", marginTop: 10 }}>
            <CustomSwitch
              checked={exclude}
              onChange={() => setExclude(!exclude)}
              inputProps={{ "aria-label": "controlled" }}
            />
            <TextView
              variant="text"
              style={{
                marginTop: 7,
              }}
            >
              {COLLECTION_DIALOG_COPY.hideContentsLabel}
            </TextView>
            <Tooltip title={COLLECTION_DIALOG_COPY.hideContentsTooltip}>
              <ToolTipStyled>i</ToolTipStyled>
            </Tooltip>
          </div>
          {/* End Exclude toggle */}

          <WheelVisibilityOptions
            name="collection-visibility"
            value={visibility}
            onChange={handleVisibilityChange}
            label="Visibility to:"
          />
          <Divider />
          <DialogActions>
            <div style={{ marginTop: 10 }}>
              <ReuseableButton
                onClick={handleCancel}
                text={"Cancel"}
                fullWidth={false}
                variant="text"
                textColor="black"
                customStyles={{
                  padding: "4px 20px",
                }}
              />

              <ReuseableButton
                text={folderId ? "Update" : "Create"}
                fullWidth={false}
                disabled={updateDisabled}
                aria-disabled={updateDisabled}
                customStyles={{
                  // DL-004 reach (ISP-4018): white-text fill -> on-fill token.
                  backgroundColor: updateDisabled
                    ? colors.disabled
                    : tokens.brand.primaryActive,
                  // gordon-0921 Update-dirty-gate: the app's disabled button
                  // token (same colors.disabled/colors.mediumGrey pair
                  // HelpPanel.jsx's send IconButton uses), not a bespoke color.
                  "&.Mui-disabled": {
                    backgroundColor: colors.disabled,
                    color: colors.mediumGrey,
                  },
                  padding: "4px 20px",
                }}
                textColor={updateDisabled ? colors.mediumGrey : undefined}
                onClick={handleCreate}
              />
            </div>
          </DialogActions>
        </DialogContent>
      </StyledDialog>
    </>
  );
};

export default CreateFolderDialog;
